import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';
import { X } from 'lucide-react';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
}

export const Sheet = ({ isOpen, onClose, children, title }: SheetProps) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Callers pass `onClose` as a fresh inline arrow on every render. If the effect
  // below depended on it, the effect would re-run on EVERY keystroke typed into a
  // form inside the sheet and pull focus away from the text box each time (on a
  // phone that closes the keyboard after every character). So keep the latest
  // `onClose` in a ref and let the effect depend on `isOpen` alone.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return;

    // Remember what had focus so it can be handed back when the sheet closes.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Lock body scroll
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus INTO the dialog, once, when it opens. The dialog itself takes
    // focus (not its first control): focusing a text box would pop the phone
    // keyboard open before the person has read anything.
    sheetRef.current?.focus();

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEscape);
      previouslyFocused?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'sheet-title' : undefined}
        className={cn(
          'fixed bottom-0 left-0 right-0 bg-surface rounded-t-2xl shadow-lg z-50 animate-in slide-in-from-bottom-4 duration-300',
          'max-h-[90vh] flex flex-col outline-none'
        )}
        style={{
          paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
        }}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-line">
            <h2 id="sheet-title" className="text-section font-semibold text-ink">
              {title}
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1 rounded-control hover:bg-neutral-tint transition-colors"
            >
              <X size={20} className="text-muted" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>
      </div>
    </div>
  );
};
